import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";

const defaultForm = {
  name: "",
  description: "",
  target_description: "",
  ai_instructions: "",
  max_daily_messages: 5,
  messages_per_contact: 1,
  mode: "visit",
  hour_start: 8,
  hour_end: 18,
};

export default function SegmentFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateInfo, setTemplateInfo] = useState("");
  const [plan, setPlan] = useState("freemium");
  const isFreemium = plan !== "premium";

  useEffect(() => {
    api.get("/api/auth/me").then((u) => setPlan((u.plan || "freemium").toLowerCase())).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/api/segments/${id}`)
        .then((data) => {
          const seg = data.segment || data;
          setForm({
            name: seg.name || "",
            description: seg.description || "",
            target_description: seg.target_description || "",
            ai_instructions: seg.ai_instructions || "",
            max_daily_messages: seg.max_daily_messages || 5,
            messages_per_contact: seg.messages_per_contact || 1,
            mode: seg.mode || "visit",
            hour_start: seg.hour_start ?? 8,
            hour_end: seg.hour_end ?? 18,
          });
        })
        .catch(() => setError("Impossible de charger le segment"))
        .finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value, 10) || 1 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isEdit) {
        await api.put(`/api/segments/${id}`, form);
      } else {
        await api.post("/api/segments", form);
      }
      navigate("/segments");
    } catch (err) {
      setError(err.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplatesFromProfile = async () => {
    setTemplateInfo("");
    setLoadingTemplates(true);
    try {
      const exclude = templates
        .map((t) => t.name)
        .filter(Boolean)
        .slice(0, 12)
        .join(",");
      const qs = exclude ? `?exclude=${encodeURIComponent(exclude)}` : "";
      const data = await api.get(`/api/segments/templates/from-profile${qs}`);
      const items = data.templates || [];
      setTemplates(items);
      setTemplateInfo(
        items.length
          ? `Nouvelles suggestions depuis votre profil${data.profile_headline ? ` · ${data.profile_headline}` : ""}`
          : "Aucun template généré pour le moment."
      );
    } catch (err) {
      setTemplateInfo(err.message || "Impossible de générer des templates depuis le profil.");
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const applyTemplate = (tpl) => {
    const allowed = isFreemium ? ["visit", "connect"] : ["visit", "connect", "invitation", "dm"];
    let mode = tpl.mode || "visit";
    if (!allowed.includes(mode)) mode = "visit";
    setForm((prev) => ({
      ...prev,
      name: tpl.name || prev.name,
      description: tpl.description || prev.description,
      target_description: tpl.target_description || prev.target_description,
      ai_instructions:
        mode === "invitation" || mode === "dm"
          ? tpl.ai_instructions || prev.ai_instructions
          : "",
      mode,
      max_daily_messages: tpl.max_daily_messages || prev.max_daily_messages,
    }));
    setTemplateInfo(`Template appliqué : ${tpl.name || "segment"}`);
  };

  if (fetching) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 animate-pulse space-y-4">
          <div className="h-6 bg-ink/5 rounded w-1/3" />
          <div className="h-10 bg-ink/5 rounded" />
          <div className="h-10 bg-ink/5 rounded" />
          <div className="h-24 bg-ink/5 rounded" />
          <div className="h-24 bg-ink/5 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-ink tracking-tight">
          {isEdit ? "Modifier le segment" : "Nouveau segment"}
        </h1>
        <p className="text-sm text-mute mt-1">
          {isEdit
            ? "Mettez à jour les paramètres de ce segment"
            : "Définissez votre audience cible et les instructions pour l'IA"}
        </p>
      </div>

      <div className="card p-8">
        {error && (
          <div className="mb-6 p-3 bg-rouge/5 border border-rouge/15 rounded-lg text-sm text-rouge">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Templates — en haut pour démarrer vite */}
          {!isEdit && (
            <div className="rounded-xl border border-line bg-cream/60 p-4">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <p className="type-label-accent">Templates intelligents</p>
                  <p className="text-xs text-mute mt-1">
                    Propose des segments adaptés à votre profil LinkedIn — chaque clic renouvelle les idées.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={loadTemplatesFromProfile}
                  disabled={loadingTemplates}
                  className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-2 disabled:opacity-70"
                >
                  {loadingTemplates ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Génération…
                    </>
                  ) : templates.length > 0 ? (
                    "Proposer d'autres templates"
                  ) : (
                    "Proposer des templates"
                  )}
                </button>
              </div>

              {loadingTemplates && (
                <div className="mt-4 space-y-2" aria-live="polite" aria-busy="true">
                  <p className="text-xs text-ink/50 mb-2">
                    Analyse de votre profil LinkedIn et création de nouvelles cibles…
                  </p>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-line bg-paper px-3 py-3 animate-pulse"
                      style={{ animationDelay: `${i * 120}ms` }}
                    >
                      <div className="h-3.5 bg-ink/10 rounded w-2/5 mb-2" />
                      <div className="h-2.5 bg-ink/5 rounded w-4/5" />
                    </div>
                  ))}
                </div>
              )}

              {!loadingTemplates && templateInfo && (
                <p className="text-xs text-ink/70 mt-3">{templateInfo}</p>
              )}
              {!loadingTemplates && templates.length > 0 && (
                <div className="mt-3 space-y-2">
                  {templates.map((tpl, idx) => {
                    const modeLabel =
                      {
                        dm: "Message direct",
                        invitation: "Invitation",
                        connect: "Connexion",
                        visit: "Visite",
                      }[tpl.mode] || tpl.mode;
                    return (
                      <button
                        key={`${tpl.name}-${idx}-${tpl.mode}`}
                        type="button"
                        onClick={() => applyTemplate(tpl)}
                        className="w-full text-left rounded-lg border border-line bg-paper px-3 py-2 hover:border-ink/20 transition"
                      >
                        <p className="text-sm font-medium text-ink">
                          {tpl.name}{" "}
                          <span className="text-ink/40">· {modeLabel}</span>
                        </p>
                        {tpl.description && (
                          <p className="text-xs text-mute mt-0.5 line-clamp-2">{tpl.description}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Nom */}
          <div>
            <label className="label-field" htmlFor="name">
              Nom du segment
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className="input-field"
              placeholder="Ex : CTOs SaaS France"
              value={form.name}
              onChange={handleChange}
            />
          </div>

          {/* Description */}
          <div>
            <label className="label-field" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={2}
              className="input-field resize-none"
              placeholder="Brève description du segment"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          {/* Mode */}
          <div>
            <label className="label-field">Mode d'action</label>
            <p className="text-xs text-mute mb-2">
              Choisissez comment l'IA interagit avec les contacts de ce segment
              {isFreemium && (
                <>
                  {" "}
                  — Freemium : Visite + Connexion.{" "}
                  <a href="/pricing" className="text-rouge underline">
                    Premium 15 $/mois
                  </a>
                </>
              )}
            </p>
            <div className="flex gap-3 items-stretch">
              {[
                {
                  mode: "dm",
                  title: "Message direct",
                  desc: "Envoie un DM personnalisé aux contacts existants dans votre réseau",
                  premium: true,
                },
                {
                  mode: "invitation",
                  title: "Invitation",
                  desc: "Envoie une demande de connexion avec une note personnalisée",
                  premium: true,
                },
                {
                  mode: "connect",
                  title: "Connexion",
                  desc: "Envoie une demande de connexion sans note pour agrandir votre réseau",
                  premium: false,
                },
                {
                  mode: "visit",
                  title: "Visite",
                  desc: "Visite les profils pour susciter la curiosité, sans message",
                  premium: false,
                },
              ].map((m) => {
                const locked = isFreemium && m.premium;
                return (
                  <button
                    key={m.mode}
                    type="button"
                    onClick={() => {
                      if (locked) {
                        window.location.href = "/pricing";
                        return;
                      }
                      setForm((prev) => ({ ...prev, mode: m.mode }));
                    }}
                    className={`flex-1 p-3 rounded-xl border text-sm font-medium transition-all text-left flex flex-col ${
                      form.mode === m.mode
                        ? "border-rouge bg-rouge/5 text-rouge"
                        : locked
                          ? "border-line bg-paper/50 text-ink/35"
                          : "border-line bg-paper text-ink/60 hover:border-ink/20"
                    }`}
                  >
                    <div className="font-display font-semibold tracking-tight mb-1 min-h-[2.75rem] leading-snug">
                      {m.title}
                      {locked ? (
                        <span className="block text-[10px] font-sans font-semibold uppercase tracking-wide text-rouge/80 mt-0.5">
                          Premium
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs opacity-70">{m.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cible — used for LinkedIn search / who to act on */}
          <div>
            <label className="label-field" htmlFor="target_description">
              Cible
            </label>
            <p className="text-xs text-mute mb-1.5">
              Qui cibler : l’IA transforme ce texte en recherche LinkedIn pour trouver les bons profils
              {form.mode === "visit"
                ? " à visiter"
                : form.mode === "connect"
                  ? " à connecter"
                  : form.mode === "invitation"
                    ? " à inviter"
                    : " à contacter"}
            </p>
            <textarea
              id="target_description"
              name="target_description"
              rows={4}
              required
              className="input-field resize-none"
              placeholder="Ex : CTOs et VP Engineering de startups SaaS en France, 50-200 employés, qui utilisent des solutions cloud"
              value={form.target_description}
              onChange={handleChange}
            />
          </div>

          {/* Instructions IA — only when a message is generated */}
          {(form.mode === "invitation" || form.mode === "dm") && (
            <div>
              <label className="label-field" htmlFor="ai_instructions">
                Instructions IA
              </label>
              <p className="text-xs text-mute mb-1.5">
                {form.mode === "invitation"
                  ? "Comment rédiger la note jointe à la demande de connexion"
                  : "Comment rédiger le message envoyé aux contacts"}
              </p>
              <textarea
                id="ai_instructions"
                name="ai_instructions"
                rows={5}
                required
                className="input-field resize-none"
                placeholder="Ex : Tu es un expert en cybersécurité. Approche les contacts en mentionnant un article récent qu'ils ont partagé ou un point commun. Propose un échange de 15 minutes sur leurs défis de sécurité cloud."
                value={form.ai_instructions}
                onChange={handleChange}
              />
            </div>
          )}

          {/* Actions par jour */}
          <div>
            <label className="label-field" htmlFor="max_daily_messages">
              Actions par jour
            </label>
            <p className="text-xs text-mute mb-1.5">
              Nombre de contacts traités par jour pour ce segment
            </p>
            <input
              id="max_daily_messages"
              name="max_daily_messages"
              type="number"
              min={1}
              max={50}
              required
              className="input-field w-24"
              value={form.max_daily_messages}
              onChange={handleChange}
            />
          </div>

          {/* Messages par contact (only for DM mode) */}
          {form.mode === "dm" && (
            <div>
              <label className="label-field" htmlFor="messages_per_contact">
                Découpage du message
              </label>
              <p className="text-xs text-mute mb-1.5">
                Un seul message, découpé en 1 à 3 bulles (comme Enter entre les
                phrases) pour paraître plus humain — pas 3 sujets différents.
              </p>
              <input
                id="messages_per_contact"
                name="messages_per_contact"
                type="number"
                min={1}
                max={3}
                required
                className="input-field w-24"
                value={form.messages_per_contact}
                onChange={handleChange}
              />
            </div>
          )}

          {/* Plage horaire */}
          <div>
            <label className="label-field">Plage horaire (heure de Paris)</label>
            <p className="text-xs text-mute mb-2">
              Les actions seront effectuées uniquement dans cette plage horaire
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  name="hour_start"
                  type="number"
                  min={0}
                  max={23}
                  className="input-field w-20 text-center"
                  value={form.hour_start}
                  onChange={handleChange}
                />
                <span className="text-sm text-mute">h</span>
              </div>
              <span className="text-mute">—</span>
              <div className="flex items-center gap-2">
                <input
                  name="hour_end"
                  type="number"
                  min={0}
                  max={23}
                  className="input-field w-20 text-center"
                  value={form.hour_end}
                  onChange={handleChange}
                />
                <span className="text-sm text-mute">h</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {isEdit ? "Enregistrer" : "Créer le segment"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/segments")}
              className="btn-secondary"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
